import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./SalesHistory.css";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  "https://techiohisab.com/api";
const API = `${API_BASE}/sales`;
const SALES_HISTORY_PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  "Packed",
  "Picked",
  "Dispatched",
  "In Transit",
  "Delivered",
  "Cancel",
  "Return",
  "Return Inward",
  "Return Completed",
  "Settlement Pending",
  "Settlement Done",
];

const normalizeStatus = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "Dispatched";

  const key = rawValue.toUpperCase().replace(/[\s-]+/g, "_");
  const statusMap = {
    PACK: "Packed",
    PACKED: "Packed",
    ORDERED: "Packed",
    PICKUP: "Picked",
    PICKED: "Picked",
    DISPATCH: "Dispatched",
    DISPATCHED: "Dispatched",
    IN_TRANSIT: "In Transit",
    INTRANSIT: "In Transit",
    DELIVERED: "Delivered",
    CANCEL: "Cancel",
    CANCELLED: "Cancel",
    CANCELED: "Cancel",
    RETURN: "Return",
    RETURNED: "Return",
    RETURN_INWARD: "Return Inward",
    RETURNINWARD: "Return Inward",
    RETURN_COMPLETED: "Return Completed",
    RETURNCOMPLETED: "Return Completed",
    SETTLEMENT_PENDING: "Settlement Pending",
    SETTLEMENTPENDING: "Settlement Pending",
    SETTLEMENT_DONE: "Settlement Done",
    SETTLEMENTDONE: "Settlement Done",
    RTO: "Return",
    DTO: "Return",
  };

  return statusMap[key] || rawValue;
};

const getStatusBadgeClass = (status) => {
  switch (normalizeStatus(status)) {
    case "Packed":
    case "In Transit":
      return "sales-history-status sales-history-status-blue";
    case "Picked":
    case "Dispatched":
    case "Delivered":
    case "Settlement Done":
      return "sales-history-status sales-history-status-green";
    case "Cancel":
      return "sales-history-status sales-history-status-red";
    case "Return":
    case "Return Inward":
    case "Return Completed":
    case "Settlement Pending":
      return "sales-history-status sales-history-status-orange";
    default:
      return "sales-history-status sales-history-status-gray";
  }
};

const getDateKey = (value) => String(value || "").slice(0, 10);
const isDateWithinRange = (dateValue, startDate, endDate) => {
  const dateKey = getDateKey(dateValue);
  if (!dateKey) return false;

  if (!startDate && !endDate) return true;
  if (startDate && !endDate) return dateKey >= startDate;
  if (!startDate && endDate) return dateKey <= endDate;

  const rangeStart = startDate <= endDate ? startDate : endDate;
  const rangeEnd = endDate >= startDate ? endDate : startDate;

  return dateKey >= rangeStart && dateKey <= rangeEnd;
};
const SALE_TYPE_LABELS = {
  SINGLE: "Single",
  MULTIPLE: "Multiple",
  COMBO: "Combo",
};

const normalizeSaleType = (value) => {
  const type = String(value || "SINGLE").trim().toUpperCase();
  return SALE_TYPE_LABELS[type] ? type : "SINGLE";
};

const formatSaleType = (value) => SALE_TYPE_LABELS[normalizeSaleType(value)] || "Single";

const formatHistoryDate = (value) => {
  const dateKey = getDateKey(value);
  if (!dateKey) return "No date";

  const [year, month, day] = dateKey.split("-");
  if (!year || !month || !day) return dateKey;

  return `${day}-${month}-${year.slice(-2)}`;
};

const getItemLabel = (row) => {
  const rawItems = String(row?.items || row?.items_summary || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (rawItems.length === 0) {
    return "-";
  }

  const uniqueItems = [];
  const seen = new Set();

  for (const item of rawItems) {
    const normalized = item.toLowerCase();
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    uniqueItems.push(item);
  }

  const isComboSale = normalizeSaleType(row?.sale_type || row?.saleType) === "COMBO";
  const label = isComboSale ? uniqueItems.join(", ") : uniqueItems[0] || "-";
  const qty = Number(row?.total_qty || row?.quantity || 0);

  if (!isComboSale && qty > 1 && !/\(\d+(?:\.\d+)?\)\s*$/.test(label)) {
    return `${label} (${qty})`;
  }

  return label;
};

const getRowTotal = (row) => {
  if (row?.amount !== undefined && row?.amount !== null) {
    return Number(row.amount || 0);
  }

  return Number(row?.total_amount || 0) + Number(row?.packaging_cost || 0);
};

const getRowQuantity = (row) => {
  const qty = Number(row?.total_qty || row?.quantity || row?.qty || 0);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
};

export default function SalesHistory() {
  const navigate = useNavigate();
  const todayDate = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesPage, setSalesPage] = useState(1);
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(todayDate);
  const [filterToDate, setFilterToDate] = useState(todayDate);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterAWB, setFilterAWB] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterAccount, setFilterAccount] = useState("");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Sales history error:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/accounts`);
        setAccounts(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error("Sales history accounts error:", error);
        setAccounts([]);
      }
    };

    fetchAccounts();
  }, []);

  const resetFilters = () => {
    setFilterPlatform("");
    setFilterFromDate("");
    setFilterToDate("");
    setFilterStatus("");
    setFilterAWB("");
    setFilterProduct("");
    setFilterCustomer("");
    setFilterAccount("");
  };

  const displayRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        _status: normalizeStatus(row.status || row.tracking_status),
        _itemLabel: getItemLabel(row),
      })),
    [rows]
  );

  const accountOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    accounts.forEach((account) => {
      const name = String(account.account_name || account.name || "").trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      options.push({ id: account.id || name, account_name: name });
    });

    displayRows.forEach((row) => {
      const name = String(row.account_name || "").trim();
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      options.push({ id: name, account_name: name });
    });

    return options;
  }, [accounts, displayRows]);

  const filteredRows = useMemo(() => {
    return displayRows.filter((row) => {
      if (
        filterPlatform &&
        String(row.platform || "DIRECT").toUpperCase() !== filterPlatform.toUpperCase()
      ) {
        return false;
      }
      if (filterStatus && normalizeStatus(row._status) !== normalizeStatus(filterStatus)) {
        return false;
      }
      if (
        filterAWB &&
        !String(row.awbNumber || row.awb_no || row.awb || "")
          .toLowerCase()
          .includes(filterAWB.toLowerCase())
      ) {
        return false;
      }
      if (
        filterProduct &&
        !String(row._itemLabel || "").toLowerCase().includes(filterProduct.toLowerCase())
      ) {
        return false;
      }
      if (
        filterCustomer &&
        !String(row.customer_name || row.customerName || "")
          .toLowerCase()
          .includes(filterCustomer.toLowerCase())
      ) {
        return false;
      }
      if (
        filterAccount &&
        !String(row.account_name || row.accountName || "")
          .toLowerCase()
          .includes(filterAccount.toLowerCase())
      ) {
        return false;
      }
      if (!isDateWithinRange(row.sale_date || row.created_at, filterFromDate, filterToDate)) {
        return false;
      }
      return true;
    });
  }, [
    displayRows,
    filterPlatform,
    filterStatus,
    filterAWB,
    filterProduct,
    filterCustomer,
    filterAccount,
    filterFromDate,
    filterToDate,
  ]);

  useEffect(() => {
    setSalesPage(1);
  }, [
    filterPlatform,
    filterFromDate,
    filterToDate,
    filterStatus,
    filterAWB,
    filterProduct,
    filterCustomer,
    filterAccount,
  ]);

  const salesPageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / SALES_HISTORY_PAGE_SIZE)
  );
  const paginatedRows = filteredRows.slice(
    (salesPage - 1) * SALES_HISTORY_PAGE_SIZE,
    salesPage * SALES_HISTORY_PAGE_SIZE
  );

  useEffect(() => {
    if (salesPage > salesPageCount) {
      setSalesPage(salesPageCount);
    }
  }, [salesPage, salesPageCount]);

  return (
    <div className="sales-history-page">
      <div className="sales-history-topbar">
        <div>
          <h2>Sales History</h2>
          <p>Sales records in the same view style as Sales Management.</p>
        </div>

        <div className="sales-history-topbar-actions">
          <button
            type="button"
            className="sales-history-top-btn sales-history-refresh-btn"
            onClick={fetchRows}
          >
            Refresh
          </button>
          <button
            type="button"
            className="sales-history-top-btn sales-history-back-btn"
            onClick={() => navigate("/sales")}
          >
            Back To Sales
          </button>
        </div>
      </div>

      <div className="sales-history-note">
        Sales history table now follows the same lower-table view used on the Sales page.
      </div>

      <div className="sales-history-filters-form">
        <select
          value={filterPlatform}
          onChange={(event) => setFilterPlatform(event.target.value)}
        >
          <option value="">All Platforms</option>
          <option value="DIRECT">Direct</option>
          <option value="FLIPKART">Flipkart</option>
          <option value="AMAZON">Amazon</option>
          <option value="MEESHO">Meesho</option>
          <option value="WEBSITE">Website</option>
        </select>

        <input
          type="date"
          value={filterFromDate}
          onChange={(event) => setFilterFromDate(event.target.value)}
          placeholder="From"
        />
        <input
          type="date"
          value={filterToDate}
          onChange={(event) => setFilterToDate(event.target.value)}
          placeholder="To"
        />

        <select value={filterStatus} onChange={(event) => setFilterStatus(event.target.value)}>
          <option value="">All Status</option>
          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>

        <select value={filterAccount} onChange={(event) => setFilterAccount(event.target.value)}>
          <option value="">All Accounts</option>
          {accountOptions.map((account) => (
            <option key={account.id} value={account.account_name}>
              {account.account_name}
            </option>
          ))}
        </select>

        <input
          placeholder="Filter by AWB"
          value={filterAWB}
          onChange={(event) => setFilterAWB(event.target.value)}
        />
        <input
          placeholder="Filter by Product"
          value={filterProduct}
          onChange={(event) => setFilterProduct(event.target.value)}
        />
        <input
          placeholder="Filter by Customer"
          value={filterCustomer}
          onChange={(event) => setFilterCustomer(event.target.value)}
        />

        <button type="button" className="sales-history-filters-reset-btn" onClick={resetFilters}>
          Reset Filters
        </button>
      </div>

      <div className="sales-history-card">
        <div className="sales-history-scroll">
          <table className="sales-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Platform</th>
                <th>Account</th>
                <th>AWB</th>
                <th>Sale Type</th>
                <th>Items</th>
                <th>Qty</th>
                <th>Packaging Material</th>
                <th>Packaging Cost</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="11" className="sales-history-empty">
                    Loading sales history...
                  </td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((row, index) => (
                  <tr
                    key={`${row.id || "sale"}-${
                      row.sales_awb_id || row.awb_no || row.awbNumber || "row"
                    }-${(salesPage - 1) * SALES_HISTORY_PAGE_SIZE + index}`}
                  >
                    <td className="sales-history-date-cell">
                      {formatHistoryDate(row.sale_date || row.created_at)}
                    </td>
                    <td>{row.platform || "DIRECT"}</td>
                    <td>{row.account_name || row.customer_name || "-"}</td>
                    <td className="sales-history-awb-cell">{row.awb_no || "N/A"}</td>
                    <td>{formatSaleType(row.sale_type || row.saleType)}</td>
                    <td className="sales-history-item-cell">{row._itemLabel}</td>
                    <td className="sales-history-qty-cell">{getRowQuantity(row)}</td>
                    <td>{row.packaging_material || "-"}</td>
                    <td>Rs.{Number(row.packaging_cost || 0).toFixed(2)}</td>
                    <td className="sales-history-total-cell">
                      Rs.{getRowTotal(row).toFixed(2)}
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(row._status)}>{row._status}</span>
                    </td>
                  </tr>
                ))}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan="11" className="sales-history-empty">
                    No sales found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > SALES_HISTORY_PAGE_SIZE && (
          <div className="sales-history-pagination">
            <button
              type="button"
              className="sales-history-pagination-btn"
              disabled={salesPage === 1}
              onClick={() => setSalesPage((page) => Math.max(1, page - 1))}
            >
              Prev
            </button>
            {Array.from({ length: salesPageCount }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={`sales-history-pagination-btn ${salesPage === page ? "active" : ""}`}
                onClick={() => setSalesPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="sales-history-pagination-btn"
              disabled={salesPage === salesPageCount}
              onClick={() => setSalesPage((page) => Math.min(salesPageCount, page + 1))}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
